import { importeDeTexto } from './dineroDeTexto.js';
// Shared helpers used across feature components extracted from deskte-full.jsx.
// Keep this file dependency-free so any component can import without cycles.

export const uid = () => Math.random().toString(36).substr(2, 9);

// User-selectable display format for dates (Settings → Preferences → Date
// Format). A module-level singleton so the plain `fmt(value)` calls scattered
// across every component honor the preference WITHOUT prop-drilling — seeded
// from the user's uiPrefs on login via setDateFormatPref and re-applied on
// change. Default 'MMM D, YYYY' preserves the historic "Feb 10, 1964" look so
// existing users see no change unless they pick a numeric format.
let _dateFmt = 'MMM D, YYYY';
export const setDateFormatPref = (v) => { if (v && typeof v === 'string') _dateFmt = v; };
export const getDateFormatPref = () => _dateFmt;

// User-selectable order for showing a client's name in the clients list +
// alphabetical sort (Settings → Preferences → "Name order"). Some agents want
// "Nombre Apellido", others "Apellido, Nombre" (Lissette 2026-06-17). Same
// singleton pattern as the date format: seeded from uiPrefs on login, re-applied
// on change, no prop-drilling. Default 'first-last' keeps the historic look.
let _nameOrder = 'first-last'; // 'first-last' | 'last-first'
export const setNameOrderPref = (v) => { _nameOrder = v === 'last-first' ? 'last-first' : 'first-last'; };
export const getNameOrderPref = () => _nameOrder;

/**
 * EL ORDEN QUE MANDA: el de la AGENCIA si lo fijó, si no el de la persona.
 *
 * Lissette, 2026-09-15: *"tiene que ser según la configuración de la agencia"*
 * → *"sí, que sea de la agencia"*. Era por usuario, y la propia pantalla lo
 * decía (*"Aplica solo a tu cuenta"*): la dueña veía "López, María" y su
 * asistente "María López" en la MISMA lista, sin que ninguna de las dos lo
 * supiera. Misma historia que los plazos de aviso, y misma solución.
 *
 * `null`/vacío en la agencia NO es "usa el de fábrica": es **no lo he fijado**,
 * y entonces se respeta lo que cada persona tuviera puesto. Esa distinción es
 * la que hace que el día del despliegue nadie vea su lista cambiar sola.
 *
 * Gemelo de `conPlazosDeLaAgencia`, más abajo.
 */
export function ordenDeNombreQueManda(ajustesDeAgencia, userPrefs) {
  const deLaAgencia = ajustesDeAgencia && ajustesDeAgencia.nameOrder;
  if (deLaAgencia === 'first-last' || deLaAgencia === 'last-first') return deLaAgencia;
  const dePersona = userPrefs && userPrefs.nameOrder;
  return dePersona === 'last-first' ? 'last-first' : 'first-last';
}

/**
 * Quién puede CAMBIARLO: los mismos que cambian los plazos de aviso — el dueño
 * de la agencia, el `agency_admin` (un dueño promovido) y el agente en
 * solitario. Los demás lo VEN, bloqueado: esconderlo haría creer que ya no
 * existe, y es lo que explica por qué su lista se lee como se lee.
 *
 * Espejo de `requireOwnerOrSolo()` en el servidor, que es quien manda de verdad.
 */
export const puedeCambiarElOrdenDelNombre = (currentUser) => puedeCambiarLosPlazos(currentUser);

// Compose a client's display name honoring the per-user order preference.
// 'last-first' → "Apellido, Nombre Segundo"; default → "Nombre Segundo Apellido".
// The optional middle name (Lissette 2026-06-22: "quiero que salga en la lista
// y en la ficha si lo tiene") sits between first and last and is dropped when
// empty. Falls back gracefully when only one of the names is present.
export const formatClientName = (c, order) => {
  if (!c) return '';
  const fn = (c.firstName || '').trim();
  const mn = (c.middleName || '').trim();
  const ln = (c.lastName || '').trim();
  const ord = order || _nameOrder;
  if (ord === 'last-first') {
    const first = [fn, mn].filter(Boolean).join(' ');
    return [ln, first].filter(Boolean).join(', ') || first || ln;
  }
  return [fn, mn, ln].filter(Boolean).join(' ');
};

// Per-user "collaborating agents" defined in Mi Perfil — [{ name, lastName, npn }].
// Singleton (same pattern as the date/name prefs) seeded from uiPrefs so the
// policy form's Agente toggle and the signature collaborator selector can offer
// them + auto-fill the NPN, with no prop-drilling (Lissette 2026-06-18).
let _collaborators = [];
export const setCollaboratorsPref = (list) => { _collaborators = Array.isArray(list) ? list : []; };
export const getCollaboratorsPref = () => _collaborators;
// Display label for a collaborator: "Nombre Apellido".
export const collaboratorLabel = (co) => `${co?.name || ''} ${co?.lastName || ''}`.trim();

// Always pin date-only strings ('YYYY-MM-DD') to LOCAL midnight; parse full ISO
// timestamps as-is so appending 'T00:00:00' never yields "Invalid Date" (the
// DocFolder bug, Lissette 2026-06-01). The display style follows the per-user
// Date Format preference; en-US text ("Feb 10, 1964") is the default.
export const fmt = (d) => {
  if (!d) return '--';
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
  if (isNaN(dt.getTime())) return '--';
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  const yyyy = dt.getFullYear();
  switch (_dateFmt) {
    case 'MM/DD/YYYY': return `${mm}/${dd}/${yyyy}`;
    case 'DD/MM/YYYY': return `${dd}/${mm}/${yyyy}`;
    case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`;
    case 'MMM D, YYYY':
    default: return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
};

// ─── Edad ────────────────────────────────────────────────────────────────────
/**
 * Edad cumplida hoy a partir de una fecha de nacimiento "YYYY-MM-DD".
 * Devuelve null si no hay fecha, si no se entiende, o si sale un imposible
 * (fecha futura / más de 130 años) — así una fecha mal tecleada no pinta
 * "-3 años" al lado del campo.
 *
 * Se lee por PARTES LOCALES a propósito: `new Date("1990-04-12")` es medianoche
 * UTC y en Florida cae en el día 11, que es justo el error que corre la
 * fecha un día. Y la edad se cuenta por calendario, no dividiendo
 * milisegundos entre 31557600000: esa división se equivoca en un año cerca del
 * cumpleaños cuando hay muchos bisiestos de por medio.
 */
export function ageFromDob(dob, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dob || '').trim());
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = +m[3];
  const bd = new Date(y, mo - 1, d);
  // Rechaza un 31 de febrero y cualquier cosa que el Date haya "corregido".
  if (bd.getFullYear() !== y || bd.getMonth() !== mo - 1 || bd.getDate() !== d) return null;
  let age = now.getFullYear() - y;
  if (now.getMonth() < mo - 1 || (now.getMonth() === mo - 1 && now.getDate() < d)) age--;
  if (age < 0 || age > 130) return null;
  return age;
}

/**
 * La edad lista para pegar junto a una etiqueta: " · 34 años" / " · 34 yrs".
 * Cadena vacía cuando no hay una edad que enseñar, para que la etiqueta quede
 * exactamente como estaba.
 */
export function ageLabelSuffix(dob, es) {
  const age = ageFromDob(dob);
  if (age === null) return '';
  return es ? ` · ${age} año${age === 1 ? '' : 's'}` : ` · ${age} yr${age === 1 ? '' : 's'}`;
}

// ─── Con cuánta antelación avisar ────────────────────────────────────────────
// Cada aviso tenía su plazo escrito a mano y repartido por el archivo: licencias
// 60 días, renovaciones 30, tareas 3, y los tickets sin plazo ninguno. Nadie
// podía cambiarlos, y el plazo bueno depende del negocio — quien renueva
// Medicare quiere ver la renovación con meses, no con 30 días
// (Lissette 2026-08-17: "que el cliente coloque él mismo cuándo quiere que le
// avises").
//
// Los valores por defecto son EXACTAMENTE lo que hacía antes, así que a quien
// no toque nada no le cambia la pantalla.
// `cita` va en MINUTOS, no en días: los otros cuatro avisan de algo que se
// prepara con tiempo, y una cita avisa de algo que empieza hoy a una hora
// (Manuela Herrera, 2026-09-09). La unidad de cada aviso vive en
// ALERT_LEAD_UNITS, y el servidor la vigila en utils/alertLead.ts.
// `citaCliente` es el recordatorio por correo AL CLIENTE (horas o días; en 0 no
// se manda). Lo lee el servidor: utils/alertLead.ts#minutosDeRecordatorioAlCliente.
export const ALERT_LEAD_DEFAULTS = { license: 60, renewal: 30, task: 3, ticket: 0, cita: 30, citaCliente: 24 };
export const ALERT_LEAD_UNITS = { license: 'd', renewal: 'd', task: 'd', ticket: 'd', cita: 'min', citaCliente: 'h' };
// Tope de 10 años: un número absurdo no debe convertir el panel de avisos en la
// lista entera de la agencia.
export const ALERT_LEAD_MAX_DAYS = 3650;

/**
 * Días de antelación configurados para un tipo de aviso.
 * Se guarda como `{ n, unit }` con unit 'd' (días) o 'm' (meses) para poder
 * enseñarlo tal cual lo escribió la persona, y aquí se resuelve a días — que es
 * lo único que sabe comparar el resto del código. Un mes = 30 días: es un aviso,
 * no una factura.
 */
export function alertLeadDays(userPrefs, key) {
  const fallback = ALERT_LEAD_DEFAULTS[key] ?? 0;
  const cfg = userPrefs && userPrefs.alertLead ? userPrefs.alertLead[key] : null;
  if (!cfg) return fallback;
  // Casilla vacía NO es cero. `Number('')` da 0, y un 0 aquí significa "no me
  // avises": borrar el número para reescribirlo habría apagado el aviso sin
  // decir nada. Vacío = no configurado; el 0 escrito a propósito sí vale (es lo
  // que deja pasar todos los tickets).
  if (cfg.n === '' || cfg.n === null || cfg.n === undefined) return fallback;
  const n = Number(cfg.n);
  if (!Number.isFinite(n) || n < 0) return fallback;
  const days = cfg.unit === 'm' ? Math.round(n * 30) : Math.round(n);
  return Math.min(days, ALERT_LEAD_MAX_DAYS);
}

/**
 * Igual que `alertLeadDays`, pero para un TIPO de póliza concreto.
 * Salud no se renueva como auto ni como vida: la agente quiere ver la de salud
 * con 90 días y el auto con otro plazo (Lissette 2026-08-17). Si ese tipo no
 * tiene nada propio, manda el plazo general de renovaciones — así configurar
 * uno solo no obliga a configurarlos todos.
 */
export function renewalLeadDaysForType(userPrefs, policyType) {
  const perType = userPrefs && userPrefs.alertLeadByType ? userPrefs.alertLeadByType[policyType] : null;
  if (perType) {
    // Se reutiliza el mismo lector para heredar sus reglas (vacío != cero,
    // negativo o texto vuelven al valor general, tope de 10 años).
    const asGeneral = alertLeadDays({ alertLead: { renewal: perType } }, 'renewal');
    // `alertLeadDays` cae en el DEFAULT del sistema cuando el valor no sirve;
    // aquí lo que tiene que mandar es el general que haya puesto la persona.
    const usable = perType.n !== '' && perType.n !== null && perType.n !== undefined
      && Number.isFinite(Number(perType.n)) && Number(perType.n) >= 0;
    if (usable) return asGeneral;
  }
  return alertLeadDays(userPrefs, 'renewal');
}

/**
 * Cuántos días hacia adelante enseña el módulo de Renovaciones.
 *
 * Lissette 2026-08-25: *"cuando coloco las alertas de las pólizas no se están
 * yendo al módulo de renovación, sigue por default 90 días y no como uno lo
 * coloca en preferencias"*. La ventana estaba clavada en 90 y no miraba nada.
 *
 * El matiz está en el valor por defecto: `ALERT_LEAD_DEFAULTS.renewal` son 30
 * días, y este módulo siempre enseñó 90. Aplicar el plazo general a secas le
 * habría BORRADO de la pantalla dos meses de renovaciones a quien nunca entró
 * en Preferencias — arreglar la queja creando una peor. Por eso la preferencia
 * solo manda cuando la persona ESCRIBIÓ un número (para ese tipo o el general);
 * sin nada escrito se devuelve `fallbackDays`, que es lo que hacía antes.
 */
export function renewalWindowDays(userPrefs, policyType, fallbackDays) {
  const escrito = (cfg) => !!cfg && cfg.n !== '' && cfg.n !== null && cfg.n !== undefined
    && Number.isFinite(Number(cfg.n)) && Number(cfg.n) >= 0;
  const porTipo = userPrefs && userPrefs.alertLeadByType ? userPrefs.alertLeadByType[policyType] : null;
  const general = userPrefs && userPrefs.alertLead ? userPrefs.alertLead.renewal : null;
  if (escrito(porTipo) || escrito(general)) return renewalLeadDaysForType(userPrefs, policyType);
  return fallbackDays;
}

// ─── Los plazos de aviso son DE LA AGENCIA ───────────────────────────────────
// Lissette 2026-08-28: *"el cambio o setup de alerta solo se puede hacer desde
// el panel del agente para toda su agencia... los seat no pueden cambiarlo"*.
//
// Antes cada persona tenía el suyo dentro de sus preferencias, así que el
// asistente veía una lista de renovaciones distinta de la de su agente y
// ninguno de los dos lo sabía.

/**
 * Las preferencias con las que MIRAR LOS AVISOS: las de la persona, pero con
 * los plazos de la agencia puestos encima.
 *
 * Esto es lo que se le pasa a las pantallas de avisos, renovaciones y licencias
 * en vez de `userPrefs` a secas. Va así, y no cambiando cada lector, porque los
 * lectores (`alertLeadDays`, `renewalLeadDaysForType`, `renewalWindowDays`) son
 * cuatro y están repartidos: si uno se quedara leyendo las de la persona,
 * enseñaría otra lista y nadie lo notaría hasta que dos personas comparasen
 * pantallas. Sobrescribir las dos claves en un solo sitio hace que eso no se
 * pueda dar.
 *
 * Las claves viejas que hayan quedado en `uiPrefs` NUNCA ganan: se pisan aunque
 * la agencia no tenga nada configurado, porque en ese caso lo correcto es caer
 * en los valores de fábrica, iguales para todos, y no en lo que aquella persona
 * hubiera dejado escrito hace meses.
 */
export function conPlazosDeLaAgencia(userPrefs, ajustesDeAgencia) {
  const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
  return {
    ...(userPrefs || {}),
    alertLead: obj(ajustesDeAgencia && ajustesDeAgencia.alertLead),
    alertLeadByType: obj(ajustesDeAgencia && ajustesDeAgencia.alertLeadByType),
  };
}

/**
 * Quién puede CAMBIARLOS: el dueño de la agencia y el agente en solitario, que
 * es dueño de la suya. `agency_admin` entra porque es un dueño promovido — en
 * el servidor es un AGENCY_OWNER de verdad, y ella lo nombró: *"y si tiene
 * agent admin puede modificar el tiempo de las alertas"*.
 *
 * Los demás —agentes de la agencia y asientos— los VEN, bloqueados. Esconderlos
 * sería peor: un ajuste que desaparece se lee como que ya no existe, y el plazo
 * es justo lo que explica por qué la lista de avisos trae lo que trae.
 *
 * Espejo de `requireOwnerOrSolo()` en el servidor, que es quien manda de verdad:
 * esto solo decide si el panel se deja tocar.
 */
export const ROLES_QUE_CAMBIAN_LOS_PLAZOS = ['agency_owner', 'agency_admin', 'solo_agent'];
export function puedeCambiarLosPlazos(currentUser) {
  const rol = String((currentUser && currentUser.role) || '').toLowerCase();
  return ROLES_QUE_CAMBIAN_LOS_PLAZOS.includes(rol);
}

// Rango de años que una celda de hoja de cálculo puede traer y seguir siendo
// una fecha. Espejo de deskte-api/src/utils/importDates.ts: si cambia uno,
// cambia el otro.
export const IMPORT_MIN_YEAR = 1900;
export const IMPORT_MAX_YEAR = 2200;

// 'YYYY-MM-DD' de una celda importada, o '' si no es una fecha guardable.
//
// El bug que arregla (E-070, 2026-08-16): los importadores hacían
// `new Date(v).toISOString().slice(0, 10)` a pelo. Para un año de cinco o más
// cifras el ISO viene en forma EXPANDIDA y con signo — `+099999-01-01T...` —
// así que ese corte a 10 caracteres devolvía `"+099999-01"`. El servidor lo
// volvía a aceptar como fecha, Prisma no podía convertirlo, y el `createMany`
// se llevaba por delante las 121 filas del lote con un 500. Un "99999" mal
// puesto en la columna de vigencia bastaba.
export const importDayIso = (v) => {
  if (v == null || v === '') return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  if (y < IMPORT_MIN_YEAR || y > IMPORT_MAX_YEAR) return '';
  return d.toISOString().slice(0, 10);
};

// Whole-calendar-day difference in LOCAL time. A date-only string parses
// as UTC midnight, so the old `new Date(d) - new Date()` was off by the
// UTC offset (Florida = -4/-5h) and could report renewals/card-expiry one
// day early or late (feedback_local_day_not_utc). Compare local midnights.
export const daysUntil = (d) => {
  if (!d) return null;
  const target = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
  if (isNaN(target.getTime())) return null;
  const now = new Date();
  const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / 86400000);
};

// ── US holiday dates ──────────────────────────────────────────────────────
// Several US holidays float (e.g. Father's Day = 3rd Sunday of June), but the
// greeting templates store a FIXED month/day that drifts wrong every year and
// is also frozen inside each user's `dkt6_holidays` localStorage cache. Keying
// the rules off the stable holiday `id` (not the cached month/day) makes the
// correct date win even for users who already have a stale template list.
// weekday: 0=Sun … 6=Sat. nth: 1-based, or 'last'. month: 1-based.
const FLOATING_HOLIDAY_RULES = {
  hol_mlk:          { weekday: 1, nth: 3,      month: 1 },  // 3rd Mon of Jan
  hol_presidents:   { weekday: 1, nth: 3,      month: 2 },  // 3rd Mon of Feb
  hol_mothersday:   { weekday: 0, nth: 2,      month: 5 },  // 2nd Sun of May
  hol_memorial:     { weekday: 1, nth: 'last', month: 5 },  // last Mon of May
  hol_fathersday:   { weekday: 0, nth: 3,      month: 6 },  // 3rd Sun of Jun
  hol_labor:        { weekday: 1, nth: 1,      month: 9 },  // 1st Mon of Sep
  hol_columbus:     { weekday: 1, nth: 2,      month: 10 }, // 2nd Mon of Oct
  hol_thanksgiving: { weekday: 4, nth: 4,      month: 11 }, // 4th Thu of Nov
};

// Date of the nth (1-based) `weekday` in month0 (0-based month) of `year`.
const nthWeekdayOfMonth = (year, month0, weekday, n) => {
  const first = new Date(year, month0, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month0, 1 + offset + (n - 1) * 7);
};
// Date of the LAST `weekday` in month0 of `year`.
const lastWeekdayOfMonth = (year, month0, weekday) => {
  const last = new Date(year, month0 + 1, 0); // day 0 of next month = last day
  const offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month0, last.getDate() - offset);
};

// Resolve a holiday template's actual calendar date for `year`. Floating
// holidays (by id) compute from the rule above; everything else uses the
// stored fixed month/day. Returned at LOCAL midnight.
export const holidayDateForYear = (holiday, year) => {
  const rule = holiday && FLOATING_HOLIDAY_RULES[holiday.id];
  let d;
  if (rule) {
    d = rule.nth === 'last'
      ? lastWeekdayOfMonth(year, rule.month - 1, rule.weekday)
      : nthWeekdayOfMonth(year, rule.month - 1, rule.weekday, rule.nth);
  } else {
    d = new Date(year, (holiday?.month || 1) - 1, holiday?.day || 1);
  }
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * LA PRÓXIMA VEZ QUE CAE ESTE FESTIVO, a las `hour` locales.
 *
 * Existe porque un cumpleaños y un feriado NO se repiten igual, y tratarlos
 * igual es justo el fallo que arregló el E-293. Un cumpleaños vuelve el mismo
 * día del mes todos los años; el Día del Padre, no — es *el tercer domingo de
 * junio*, y eso es el 15 en 2025 y el 21 en 2026. Un «si ya pasó, súmale un
 * año al mismo día» acierta con el cumpleaños y falla con el feriado.
 *
 * Por eso la fecha del año siguiente se vuelve a CALCULAR con la regla en vez
 * de arrastrar el día de este año. Un feriado de fecha fija (Navidad, 4 de
 * julio) y los que añada la agencia caen solos en el camino de siempre:
 * `holidayDateForYear` los resuelve por su mes y día guardados.
 *
 * `ahora` se puede pasar para poder probarlo sin depender del día real.
 */
export const proximaFechaDeFestivo = (holiday, hour = 9, ahora = new Date()) => {
  const conHora = (year) => {
    const d = holidayDateForYear(holiday, year);
    d.setHours(hour, 0, 0, 0);
    return d;
  };
  const d = conHora(ahora.getFullYear());
  // El mismo minuto de margen que usa el encolado de cumpleaños: programar
  // algo para dentro de treinta segundos es programarlo para el pasado.
  return d.getTime() < ahora.getTime() + 60_000 ? conHora(ahora.getFullYear() + 1) : d;
};

// Formats a number as currency with thousand separators (e.g. 12345.6 → '$12,345.60').
export const fmtMoney = (v) => (v ? `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '--');

// Like fmtMoney, but renders "$0.00" for a genuine zero instead of "--".
// $0 is a legitimate value in this business (e.g. $0-premium ACA plans), so
// the premium fields use this; only null/undefined/'' count as "no value".
export const fmtMoneyZero = (v) =>
  (v === null || v === undefined || v === '')
    ? '--'
    : `$${parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Builds the "lo que ya se ha usado" list behind a free-text field with
// suggestions: trims, drops empties, and dedupes IGNORANDO mayusculas y
// acentos — pero conserva la PRIMERA forma en que alguien lo escribio.
//
// Lo importante es el deduplicado laxo. Una lista que se llena sola se pudre
// por ahi: alguien escribe "Renovaciones", otro "renovaciones" y otro
// "Renovacion ", y el desplegable acaba con tres entradas que son la misma
// cosa — y encima filtran distinto. Ordenada, para que se pueda encontrar.
export const distinctLabels = (values) => {
  const vistos = new Map();
  for (const v of values || []) {
    const s = String(v == null ? '' : v).trim();
    if (!s) continue;
    // El rango de tildes va como escape \u0300-\u036f y NO como los signos
    // literales: son marcas combinantes, invisibles en el editor, y en este
    // repo ya hubo un archivo que se volvio binario por escribir un byte raro
    // dentro del codigo en vez de escaparlo.
    const clave = s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (!vistos.has(clave)) vistos.set(clave, s);
  }
  return [...vistos.values()].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
};

// Reads a money amount typed or stored as text ('1,250.00', '$1,250', 1250)
// and gives back a number, or null when there is nothing usable. $0 is a real
// amount here (ACA plans with a full subsidy), so it comes back as 0 — only an
// empty/unparseable value is null.
/**
 * UNA CIFRA DE DINERO ESCRITA A MANO -> UN NÚMERO.
 *
 * Lissette, 2026-09-16: *"a la agencia de Lina las primas de las pólizas se le
 * vinieron con número con más ceros"*.
 *
 * Lo que había aquí tiraba las comas y se quedaba con los puntos:
 *
 *     String(v).replace(/[^0-9.-]/g, '')
 *
 * …así que una prima escrita con COMA DECIMAL —que es como se escribe en casi
 * toda Latinoamérica, y como la escribe Excel en una máquina en español— se
 * multiplicaba por cien: `"120,50"` salía **12050** y `"350,00"` salía
 * **35000**. Y al revés, `"1.200,75"` salía 1.20075. Nada avisaba: son números
 * creíbles en una columna de dinero.
 *
 * La cuenta de desambiguar —cuándo un punto o una coma es de millar y cuándo es
 * decimal— ya estaba resuelta y probada en `dineroDeTexto.js`, escrita el
 * 2026-09-14 para el ingreso del cotizador. Lo que faltaba era usarla aquí.
 * Dos reglas para la misma pregunta son dos respuestas distintas según la
 * pantalla.
 *
 * El SIGNO se conserva aparte, porque `importeDeTexto` solo mira la magnitud:
 * se acepta el menos delante y el paréntesis contable de los exports.
 */
export const parseMoney = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const negativo = /^-/.test(s) || /^\(.*\)$/.test(s);
  const n = importeDeTexto(s);
  if (n === null) return null;
  return negativo ? -n : n;
};

// "Monto desde / hasta" for the Monthly Payment table (Lissette 2026-08-26).
// Mirrors the Desde/Hasta date range of the Policies module on purpose:
//   - no bound written  → everything passes;
//   - a bound written   → a row with NO amount drops out. It cannot be shown to
//     be inside the range, and letting it through would make "from $200" list
//     policies with a blank Monto, which reads as the filter being broken.
// It does NOT swap the bounds when they come in backwards: the caller warns on
// screen instead, so nobody is left with an empty table and no explanation.
export const amountInRange = (valor, min, max) => {
  const lo = parseMoney(min);
  const hi = parseMoney(max);
  if (lo === null && hi === null) return true;
  const n = parseMoney(valor);
  if (n === null) return false;
  if (lo !== null && n < lo) return false;
  if (hi !== null && n > hi) return false;
  return true;
};

// Normalize US phones to "+1 (305) 555-1234". We always prepend the +1
// country code on 10-digit numbers so SMS/WhatsApp deep-links work without
// extra fixups. Only the digits matter — any separators the agent typed
// (dashes, dots, parens, spaces) are stripped first. Anything that isn't a
// clean 10- or 1+10-digit US number is returned unchanged so we never mangle
// a half-typed or genuinely international number. Mirrors the on-blur helper
// in deskte-full.jsx; also applied at read-time in api.mapClientFromApi so
// existing saved numbers display normalized without a DB migration.
export const formatPhoneUS = (raw) => {
  if (!raw) return raw;
  const s = String(raw).trim();
  const digits = s.replace(/\D/g, '');
  if (/^\d{10}$/.test(digits)) return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (/^1\d{10}$/.test(digits)) return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return s;
};

// Formats 'YYYY-MM' as 'Month YYYY' (e.g. '2026-04' → 'April 2026').
export const fmtMonth = (m) => {
  if (!m) return '--';
  // Guard against non-month values like "all" (filter sentinel) — without
  // this, new Date(NaN,...) renders the literal string "Invalid Date".
  const match = /^(\d{4})-(\d{2})$/.exec(String(m));
  if (!match) return '--';
  const d = new Date(+match[1], +match[2] - 1, 1);
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

// Returns months as 'YYYY-MM' strings, newest first. By default the last 24
// months (current + past). Pass `future` to also include that many UPCOMING
// months at the top, so Monthly Payments can be scheduled ahead of time
// (Lissette 2026-07-09: "escoger meses a futuro"). Default 0 keeps the old
// behavior everywhere else (Commissions, Dashboard).
export const MONTHS_LIST = (future = 0) => {
  const list = [];
  const now = new Date();
  for (let i = future; i > 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    list.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    list.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return list;
};

// ── Client tag colors (fluorescent / highlighter) ───────────────────────────
// Tags stay PLAIN strings on Client.tags. Their (optional) color lives in an
// agency-shared map keyed by the tag name, so the same tag reads the same
// color for every agent + seat. These helpers keep the lookup + rendering
// consistent across the client list, the detail header and the tag editor.

// Curated fluorescent / neon palette — reads as a highlighter when used as a
// solid chip background (black/white auto-text picks the readable contrast).
export const TAG_FLUOR_COLORS = [
  '#39ff14', // verde neón
  '#aaff00', // lima
  '#ffea00', // amarillo neón
  '#ff9500', // naranja neón
  '#ff2079', // rosa neón
  '#ff1744', // rojo neón
  '#00e5ff', // cian neón
  '#19b6ff', // azul neón
  '#b026ff', // morado neón
  '#00ffa3', // menta neón
];

const HEX6 = /^#[0-9a-fA-F]{6}$/;

// Normalize a tag name to its color-map key (matches the backend sanitizer:
// trimmed + lowercased). Empty → ''.
export const tagColorKey = (name) => String(name == null ? '' : name).trim().toLowerCase();

// Look up a tag's color in the agency map (case-insensitive by name). Returns
// '' when the tag has no assigned color (renders with the default chip look).
export const tagColorFor = (name, map) => {
  if (!map || typeof map !== 'object') return '';
  const c = map[tagColorKey(name)];
  return HEX6.test(c || '') ? c : '';
};

// Black or white text for readable contrast on a hex background (W3C relative
// luminance). Fluorescent colors are bright → mostly near-black text.
export const tagTextColor = (hex) => {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex || ''));
  if (!m) return '#04121f';
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#04121f' : '#ffffff';
};

// Inline style atoms for a tag chip given its (optional) color. A colored tag
// renders as a solid fluorescent highlight (readable in BOTH light + dark
// themes thanks to the luminance-based text); an uncolored tag keeps the
// subtle cyan look used historically.
export const tagChipStyle = (color) => {
  if (HEX6.test(color || '')) {
    return { bg: color, fg: tagTextColor(color), border: color };
  }
  return { bg: 'rgba(0,212,255,.1)', fg: 'var(--c-cyan,#00d4ff)', border: 'rgba(0,212,255,.18)' };
};

// Copy text to the clipboard WITHOUT ever throwing or rejecting. The async
// Clipboard API rejects with NotAllowedError ("Write permission denied") when
// the document isn't focused or the browser refuses the write — several copy
// buttons called writeText() with no .catch(), so the rejection escaped as an
// unhandled error in Sentry (DESKTE-SPA-22) AND the copy silently failed while
// the UI still said "¡Copiado!". This tries the modern API first, falls back to
// the legacy execCommand('copy') textarea trick (which works in the cases where
// the async API is denied), and resolves to true/false so callers can show the
// success feedback only when the text actually made it to the clipboard.
export const copyText = async (text) => {
  const s = String(text == null ? '' : text);
  if (!s) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(s);
      return true;
    }
  } catch {
    // Denied/unavailable (unfocused document, insecure context, Safari gesture
    // rules) — not fatal, the legacy path below still works in most of them.
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = s;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, s.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    // Both paths failed — report it so the caller can tell the user instead of
    // showing a false "¡Copiado!".
    return false;
  }
};

// The name outbound material should be signed with, for the CURRENT user.
// Everything a SEAT sends goes out in the AGENT's name (Lissette: "todo lo que
// manda el seat es a nombre del agente") — so for a seat we use the supervising
// agent, and if that's not loaded (stale session), we fall back to the AGENCY
// name, NEVER the seat's own name. For agents/owners it's their own name.
// Used by client-side composers (mailto greetings, signature docs) that can't
// defer to the backend; backend-sent email resolves this server-side instead.
export const presentingAgentName = (u, fallback = 'Agent') => {
  if (!u) return fallback;
  if (u.role === 'assistant') {
    return u.supervisor?.displayName || u.agency?.name || u.agencyName || fallback;
  }
  return u.name || u.displayName || fallback;
};

// Un DIA DE CALENDARIO, sacado de lo que manda el servidor sin pasar por la
// zona horaria.
//
// La vigencia de una poliza, su renovacion o el vencimiento de una factura no
// son instantes: son dias. El servidor los guarda como medianoche UTC
// (`new Date('2026-08-15')` en policies.routes.ts), asi que pintarlos con
// `new Date(iso).toLocaleDateString()` los pasa por la hora del navegador y en
// toda America los corre un dia HACIA ATRAS — la poliza que empieza el 15 se
// enseña como el 14.
//
// Es la regla de siempre de esta casa (dia local, nunca UTC) escrita como
// funcion, para no tener que acordarse de ella en cada pantalla nueva.
// Reportado el 2026-08-25 en el portal del cliente: *"no esta tomando bien la
// fecha de inicio de la poliza y renovacion"*.
//
// Devuelve un Date en la medianoche LOCAL de ese dia, o null cuando la marca
// lleva una hora de verdad — porque entonces si es un instante (una factura
// generada a las 8pm) y tiene que verse en la hora de quien mira. El que llama
// decide que hacer con el null; asi esta funcion no impone un formato.
export function calendarDayOf(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/.exec(String(iso == null ? '' : iso));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

// ---------------------------------------------------------------------------
// RENOVACIONES: que cuenta como "ya renovada".
//
// Lissette, 2026-08-28: quiere una pestaña "Renovadas" y que esas pólizas se
// vayan de la lista de trabajo. La regla vive aquí, fuera de la pantalla, por
// dos razones: para poder probarla sin arrastrar React, y para que el día que
// otro módulo necesite saber si una póliza está renovada no se escriba una
// segunda versión que diga algo distinto (E-138).
//
// El módulo mueve la póliza por cuatro etapas — PROCESO, REVISION, SOMETIDA y
// DONE — y el kit de renovación se envía al llegar a DONE. Una póliza sin etapa
// escrita todavía no ha empezado, así que cuenta como PROCESO.

/** La etapa de renovación del ciclo actual. Sin etapa = no ha empezado. */
export function etapaDeRenovacion(p) {
  return (p && p.renewalStage) || 'PROCESO';
}

/** El tope hacia atrás de la ventana de renovaciones, en días. */
export const DIAS_DE_VENTANA_POR_DEFECTO = 90;

/**
 * ¿SE RENOVÓ EN EL CICLO QUE SE ACABA DE CERRAR?
 *
 * Esta es la que faltaba, y sin ella la pestaña «Renovadas» era inalcanzable
 * (E-294). Lissette, 2026-09-19: *«cuando se marca como renovado desaparece y
 * no se va a la pestaña de renovado»*.
 *
 * ── POR QUÉ NO BASTA CON MIRAR LA ETAPA ─────────────────────────────────────
 *
 * «Marcar Renovada» hace dos cosas seguidas: pone la etapa en DONE y **mueve
 * la fecha de renovación un año adelante**. Y `renewalDate` y `expirationDate`
 * son **la misma columna**, así que ese segundo paso:
 *
 *   1. saca la póliza de la ventana (ahora renueva dentro de 365 días, no 90),
 *      y desaparece de las DOS pestañas; y
 *   2. rompe la coincidencia del ciclo, así que al recargar la etapa se borra
 *      y la póliza vuelve a contar como PENDIENTE.
 *
 * Cada uno de los dos, por su cuenta, ya bastaba para que nunca llegara a
 * «Renovadas».
 *
 * ── LA MARCA QUE SÍ SOBREVIVE ───────────────────────────────────────────────
 *
 * `renewalStageCycle` guarda **el vencimiento que se cerró** — se fija en el
 * momento de marcarla, antes de mover nada. Esa fecha no se toca nunca más, y
 * dice exactamente lo que hace falta: *«el término que terminaba el X se
 * renovó»*.
 *
 * Y se caduca sola, que es lo que pedía el diseño original («esta pestaña
 * enseña las renovadas DE ESTE CICLO, no un archivo»): se mira contra la MISMA
 * ventana que define «Pendientes». Cuando el término cerrado queda más de 90
 * días atrás, la póliza sale de la pestaña por su cuenta. Nadie tiene que
 * limpiarla.
 */
export function renovadaEnCicloCerrado(p, diasDeVentana = DIAS_DE_VENTANA_POR_DEFECTO) {
  // `renewalStageGuardada` es la etapa TAL CUAL está en la base, sin el
  // reseteo por cambio de ciclo que aplica `api.js` — que es justo el que
  // borraba esta marca. Ver el mapeo en crm-app/api.js.
  if (!p || p.renewalStageGuardada !== 'DONE') return false;
  const d = daysUntil(p.renewalStageCycle);
  if (d === null) return false;
  return d <= diasDeVentana && d >= -DIAS_DE_VENTANA_POR_DEFECTO;
}

/**
 * ¿Está renovada?
 *
 * SOLO DONE (decisión de Lissette). SOMETIDA no cuenta: está mandada a la
 * aseguradora pero todavía puede volverse atrás, y darla por renovada la
 * sacaría de la vista justo cuando hay que estar pendiente de la respuesta.
 *
 * Dos caminos, y hacen falta los dos: la etapa DEL CICLO ACTUAL (una póliza
 * movida a DONE sin tocarle la fecha) y la del CICLO YA CERRADO (la que pasó
 * por «Marcar Renovada», que sí movió la fecha). Con solo el primero, el botón
 * que más se usa no llevaba a ninguna parte.
 */
export function estaRenovada(p, diasDeVentana = DIAS_DE_VENTANA_POR_DEFECTO) {
  return etapaDeRenovacion(p) === 'DONE' || renovadaEnCicloCerrado(p, diasDeVentana);
}

/**
 * Reparte las pólizas de la ventana en las dos pestañas.
 *
 * Devuelve las dos listas SIEMPRE, aunque una venga vacía: la pantalla enseña
 * el número en las dos pestañas, y "Renovadas (0)" es una respuesta —dice que
 * todavía no has terminado ninguna— mientras que una pestaña sin número deja la
 * duda de si está vacía o rota.
 *
 * No reordena: el llamador ya trae las pólizas ordenadas por fecha.
 *
 * `diasDeVentanaDe(poliza)` dice cuántos días de antelación usa cada póliza —
 * el plazo puede ser distinto por tipo (salud con 4 meses, auto con 30 días).
 * Solo hace falta para decidir si una renovada ya se salió de este ciclo; sin
 * él se usan los 90 de siempre.
 */
export function repartirRenovaciones(polizas, diasDeVentanaDe = null) {
  const pendientes = [];
  const renovadas = [];
  const noRenuevan = [];
  for (const p of polizas || []) {
    const dias = diasDeVentanaDe ? diasDeVentanaDe(p) : DIAS_DE_VENTANA_POR_DEFECTO;
    if (etapaDeRenovacion(p) === ETAPA_NO_RENUEVA) noRenuevan.push(p);
    else (estaRenovada(p, dias) ? renovadas : pendientes).push(p);
  }
  return { pendientes, renovadas, noRenuevan };
}

/**
 * "NO VA A RENOVAR" (Lissette 2026-09-29): *"en el módulo de renovación crear
 * un status que diga no va a renovar y cuando se aplique que salga de la lista
 * y pase a terminados"*.
 *
 * Es una etapa más (el servidor acepta cualquier id), así que no hace falta
 * migración. Como las demás, va amarrada al CICLO: cuando la póliza entra en su
 * término siguiente, el servidor la devuelve sin etapa y vuelve sola a
 * Pendientes. No manda el kit: eso solo lo hace DONE.
 */
export const ETAPA_NO_RENUEVA = 'NO_RENUEVA';

/**
 * RENOVADA CON UNA PÓLIZA NUEVA (Lissette 2026-09-26).
 *
 * La agencia renueva creando OTRA póliza (plan, número o compañía nuevos) y
 * cancelando la vieja. El servidor deja en la vieja la marca
 * `customData.renovadaConId` al darla por renovada con la nueva
 * (policies.routes.ts, POST /renewal-stage). Devuelve ese id, o ''.
 */
export function renovadaConPolizaNueva(p) {
  const id = p && p.customData && p.customData.renovadaConId;
  return typeof id === 'string' ? id : '';
}

/**
 * ¿Una póliza cancelada o inactiva sigue en Renovaciones?
 *
 * Solo si se renovó con una póliza nueva y está renovada en este ciclo: sale
 * en la pestaña "Renovadas". Antes TODA cancelada desaparecía, así que
 * cancelar la vieja antes de mandar el kit borraba a la persona de la lista.
 * Una cancelada sin póliza nueva sigue fuera: no se renovó.
 */
export function canceladaPeroRenovada(p, diasDeVentana = DIAS_DE_VENTANA_POR_DEFECTO) {
  return !!renovadaConPolizaNueva(p) && estaRenovada(p, diasDeVentana);
}

// ---------------------------------------------------------------------------
// FILTROS DE VARIOS VALORES
//
// Lissette 2026-08-28: poder elegir varias aseguradoras a la vez, en Clientes y
// en Pólizas. La regla vive aquí, y no dentro de cada pantalla, porque son dos
// módulos distintos consumiéndola: escrita dos veces se separa en cuanto
// alguien toque una (E-138).

/**
 * ¿Este valor pasa el filtro?
 *
 * **Lista vacía = todas.** No estar filtrando no puede vaciar la pantalla; es
 * el caso que hay que escribir bien una sola vez, porque escrito al revés no da
 * ningún error: simplemente no devuelve nada y parece que no hay datos.
 *
 * La comparación ignora mayúsculas y espacios de sobra: la aseguradora se
 * escribe a mano en la póliza y "Ambetter" y "ambetter " son la misma.
 */
export function coincideConSeleccion(valor, seleccion) {
  if (!Array.isArray(seleccion) || seleccion.length === 0) return true;
  const v = String(valor == null ? '' : valor).trim().toLowerCase();
  return seleccion.some((s) => String(s == null ? '' : s).trim().toLowerCase() === v);
}

/** Añade o quita un valor de la selección, sin repetirlo. */
export function alternarSeleccion(seleccion, valor) {
  const lista = Array.isArray(seleccion) ? seleccion : [];
  return lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor];
}

/**
 * Qué poner en el botón del filtro.
 *
 * Con una sola elegida se enseña SU NOMBRE, no "1 seleccionada": el nombre es
 * la respuesta a "¿qué estoy viendo?" y el número obliga a abrir el panel para
 * averiguarlo.
 */
export function etiquetaDeSeleccion(seleccion, textoTodas, plantillaVarias) {
  const lista = Array.isArray(seleccion) ? seleccion : [];
  if (lista.length === 0) return textoTodas;
  if (lista.length === 1) return lista[0];
  return plantillaVarias.replace('{n}', String(lista.length));
}

/**
 * Interpreta lo que hay guardado para un filtro de varios valores.
 *
 * Se separa de la lectura del almacén a proposito: esto es la REGLA —qué
 * significa cada forma del valor— y se puede probar sin navegador. Quien lee
 * de localStorage es `useStickyFilterList`, en stickyFilter.js.
 *
 * Aguanta lo que había antes: la misma clave guardaba UNA aseguradora en texto
 * plano ("Ambetter"). Si la tiráramos por no ser JSON, a todo el que tuviera un
 * filtro puesto se le borraría solo al actualizar — y como un filtro no da
 * error, lo único que vería es que su lista cambió sin motivo.
 */
export function parsearListaGuardada(guardado) {
  if (guardado == null || guardado === '' || guardado === 'all') return [];
  try {
    const parsed = JSON.parse(guardado);
    if (Array.isArray(parsed)) return parsed.filter((v) => typeof v === 'string' && v !== '');
    // Un JSON que no es lista (un numero, un objeto) no describe una seleccion.
    if (typeof parsed !== 'string') return [];
    return parsed === '' ? [] : [parsed];
  } catch (_) { /* no era JSON: es el valor de antes, en texto plano */ }
  return [String(guardado)];
}


// ---------------------------------------------------------------------------
// QUIÉN REFIRIÓ A ESTE CLIENTE
//
// Lissette 2026-08-28: "así como tenemos una [etiqueta] que se vincula cuando
// es dependiente mayor de edad, quiero otra que se vincule automáticamente
// cuando ese cliente es referido de otro… si Juan refiere a Marco, que a Marco
// le salga una etiqueta que diga 'Referido por' automáticamente". Y al
// preguntarle: **la etiqueta lleva el nombre de quien lo refirió.**
//
// El dato ya existía en la ficha desde siempre; lo que faltaba era enseñarlo
// donde se ve de un vistazo. La regla vive aquí porque la usan la lista de
// clientes y la ficha, y escrita dos veces se separa (E-138).

/**
 * El nombre de quien refirió a este cliente, o '' si no lo refirió nadie.
 *
 * Hay DOS orígenes y el orden importa:
 *
 *   1. `referredByClientId` — el vínculo de verdad, puesto cuando el referido
 *      se creó desde la ficha de otro cliente. Se prefiere porque **sigue al
 *      cliente**: si mañana se corrige el apellido de Juan, la etiqueta de
 *      Marco se corrige sola.
 *   2. El nombre escrito a mano (`refFirstName` / `refLastName`). Es lo que hay
 *      cuando el referido se apuntó a mano, y se queda congelado.
 *
 * Si el vínculo apunta a un cliente que ya no está (borrado, fusionado), se cae
 * al nombre escrito en vez de devolver vacío: perder la etiqueta por un id
 * roto sería esconder un dato que sí tenemos.
 *
 * @param {(id:string)=>object|null|undefined} buscarPorId  Cómo encontrar al
 *        cliente que refirió. Se pasa desde fuera para que esto no dependa de
 *        cómo cada pantalla guarde su índice.
 */
export function nombreDeQuienRefirio(cliente, buscarPorId) {
  if (!cliente) return '';
  const id = String(cliente.referredByClientId || '').trim();
  if (id && typeof buscarPorId === 'function') {
    const quien = buscarPorId(id);
    if (quien) {
      const n = `${String(quien.firstName || '').trim()} ${String(quien.lastName || '').trim()}`.trim();
      if (n) return n;
    }
  }
  const nombre = `${String(cliente.refFirstName || cliente.referredByFirstName || '').trim()} `
    + `${String(cliente.refLastName || cliente.referredByLastName || '').trim()}`;
  return nombre.trim();
}

// ---------------------------------------------------------------------------
// EL ORDEN DE LOS CAMPOS EN EL CONSTRUCTOR DE REPORTES
//
// Lissette 2026-08-28, con captura del desplegable: "puedes colocar esos en
// forma alfabética dejando fijo de primero nombre apellido".
//
// El catálogo estaba en el orden en que se fue escribiendo — identidad, hogar,
// referido, cónyuge, fechas, póliza — que es el orden en que se PROGRAMÓ, no
// uno que sirva para buscar. Con 62 campos, encontrar "Aseguradora" obligaba a
// recorrer la lista entera con el ojo: no hay ningún sitio donde "debería"
// estar. Alfabético sí lo hay.
//
// Nombre y Apellido se quedan clavados arriba porque son los que se usan casi
// siempre; mandarlos al medio de la N por coherencia sería ordenar la lista
// contra quien la usa.

/** Los campos que van fijos arriba, en este orden. */
export const CAMPOS_FIJOS_DEL_REPORTE = ['firstName', 'lastName'];

/**
 * Ordena el catálogo de campos para el desplegable.
 *
 * Alfabético POR ETIQUETA, no por clave: la agente lee "Aseguradora", no
 * "policy.carrier". Y con `localeCompare` en español, para que los acentos y
 * las mayúsculas no descoloquen la lista ("Cónyuge" va con la C, no al final).
 *
 * No muta la lista que entra: el catálogo se reusa en otros sitios.
 */
export function ordenarCamposDeReporte(campos) {
  const lista = Array.isArray(campos) ? campos : [];
  const fijos = [];
  for (const clave of CAMPOS_FIJOS_DEL_REPORTE) {
    const c = lista.find((x) => x && x.key === clave);
    if (c) fijos.push(c);
  }
  const resto = lista
    .filter((c) => c && !CAMPOS_FIJOS_DEL_REPORTE.includes(c.key))
    .sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), 'es', { sensitivity: 'base' }));
  return [...fijos, ...resto];
}

// ---------------------------------------------------------------------------
// UN MEMBER ID POR PERSONA (para pintar)
//
// Lissette 2026-08-28: en varios planes de salud la aseguradora emite una
// tarjeta por persona y cada una trae su número. La póliza los guarda en
// `memberIdsByPerson`, con las mismas claves de `policyInsured` — "primary",
// "spouse" y el id de cada dependiente — para poder enseñar cada número al
// lado del nombre de quien es.
//
// La regla de qué se GUARDA vive en el servidor (utils/memberIds.ts), que es
// quien puede garantizarla en todos los caminos de escritura. Esto de aquí es
// solo lo que hace falta para PINTAR.

/**
 * El mapa listo para la pantalla.
 *
 * El titular sale del mapa si está, y si no del `memberId` de siempre. Sin esa
 * caída, una póliza guardada antes de que esto existiera —o traída por una
 * importación, que solo escribe `memberId`— saldría con la fila del titular
 * VACÍA mientras la tarjeta sí enseña su número. Dos sitios de la misma
 * pantalla diciendo cosas distintas del mismo dato.
 */
export function componerMemberIds(poliza) {
  const raw = poliza && typeof poliza.memberIdsByPerson === 'object' && poliza.memberIdsByPerson
    ? poliza.memberIdsByPerson : {};
  const mapa = {};
  for (const [k, v] of Object.entries(raw)) {
    const clave = String(k || '').trim();
    const valor = String(v == null ? '' : v).trim();
    if (clave && valor) mapa[clave] = valor;
  }
  const titular = String((poliza && poliza.memberId) || '').trim();
  if (!mapa.primary && titular) mapa.primary = titular;
  return mapa;
}

/**
 * ¿La tecla es para ESCRIBIR? Devuelve true cuando el foco está en una caja de
 * texto, un desplegable o algo editable.
 *
 * Existe para que una pantalla que atiende las flechas del teclado no se las
 * robe a quien está escribiendo. Comisiones por Póliza (Lissette 2026-09-11:
 * *"y también con las flechas del teclado"*) es una tabla LLENA de casillas
 * editables: mover la tabla mientras alguien corrige un importe con ← y → sería
 * peor que no tener las flechas.
 *
 * Vive aquí, en común, porque el día que otra tabla quiera teclado va a tener
 * exactamente el mismo problema — y una regla que cada pantalla tiene que
 * acordarse de aplicar es una regla que alguna olvidará.
 */
export function estaEscribiendo(destino) {
  if (!destino) return false;
  const etiqueta = String(destino.tagName || '').toUpperCase();
  if (etiqueta === 'INPUT' || etiqueta === 'TEXTAREA' || etiqueta === 'SELECT') return true;
  return !!destino.isContentEditable;
}
